import { type NextPage } from "next";
import { type GetServerSideProps } from "nextjs-routes";
import { type ComponentProps } from "react";
import { value } from "~/components/helpers";
import {
  type Input as MarketDataInput,
  mapMarketDataInput,
  MarketDataBrowser,
} from "~/components/market-data/MarketDataBrowser";
import { ssr } from "~/lib/ssr";
import { type GetMarketDataStatsResponse, GetMarketDataStatsSchema } from "~/pages/api/get-market-data-stats";
import { type EmployeeLocationWithCountry, getDefaultLocations } from "~/services/employee/employeeLocation";
import { getMarketDataStats } from "~/services/market-data/getMarketDataStats";
import { parseQueryString } from "~/services/market-data/market-data-filters/parseQueryString";

const handler: GetServerSideProps<Props, "/try/market-data"> = async (ctx) => {
  const initialInput = await parseQueryString(ctx.req, ctx.query, {
    canAccessFiguresAiV2: ctx.req.featureFlags.CAN_ACCESS_FIGURES_AI_V2,
    canByPassJobSelectorLimit: ctx.req.featureFlags.CAN_BYPASS_JOB_SELECTOR_LIMIT,
    companyFiltersWithMultipleValuesEnabled: ctx.req.featureFlags.CAN_USE_BENCHMARK_DATA_FILTERS_WITH_MULTIPLE_VALUES,
    useTestData: true,
  });

  const initialStats = await value(async () => {
    try {
      const input = GetMarketDataStatsSchema.validateSync({
        useTestData: true,
        ...mapMarketDataInput(initialInput),
      });

      return getMarketDataStats(ctx.req, input, ctx.req.user);
    } catch (error) {
      return null;
    }
  });

  const company = ctx.req.user?.companyId
    ? await ctx.req.prisma.company.findUniqueOrThrow({
        where: { id: ctx.req.user.companyId },
        include: {
          logo: true,
          cover: true,
          liveSurvey: true,
        },
      })
    : undefined;

  const defaultLocations = await getDefaultLocations(ctx.req);

  return {
    props: {
      company,
      initialInput,
      initialStats,
      defaultLocations,
    },
  };
};

export const getServerSideProps = ssr(handler, {
  authentication: { optional: true },
});

type Props = {
  company?: ComponentProps<typeof MarketDataBrowser>["company"];
  initialInput: MarketDataInput;
  initialStats: GetMarketDataStatsResponse | null;
  defaultLocations: EmployeeLocationWithCountry[];
};

const Page: NextPage<Props> = ({ company, initialInput, initialStats, defaultLocations }) => {
  return (
    <MarketDataBrowser
      useTestData
      company={company}
      defaultLocations={defaultLocations}
      initialInput={initialInput}
      initialStats={initialStats}
      title="Try the app"
      filtersPresets={[]}
    />
  );
};

export default Page;
